import Link from "next/link";
import type { DefaultValues } from "react-hook-form";
import type { ServiceContactFormDataT } from "@/app/schemas/zod/contact-form-schema";
import type {
  ContactFormConfigT,
  ContactFormFieldConfigT,
} from "./contact-form-types";

export type ServiceContactFormFieldNameT =
  | "company"
  | "salutation"
  | "firstname"
  | "lastname"
  | "email"
  | "phone"
  | "location"
  | "make"
  | "vehicleLicensePlate"
  | "vehicleVin"
  | "vehicleMileage"
  | "vehicleModel"
  | "comments"
  | "privacy"
  | "mail"
  | "case"
  | "URL"
  | "source"
  | "dealerdesk";

export const serviceContactFormDefaultValues: DefaultValues<ServiceContactFormDataT> =
  {
    company: undefined,
    salutation: undefined,
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    location: undefined,
    make: undefined,
    vehicleLicensePlate: undefined,
    vehicleVin: undefined,
    vehicleMileage: undefined,
    vehicleModel: undefined,
    comments: undefined,
    privacy: false,
    mail: "",
    case: "CASE",
    URL: undefined,
    source: "Service Anfrage",
    dealerdesk: "Service",
  };

const primaryFields: ContactFormFieldConfigT<ServiceContactFormFieldNameT>[] = [
  {
    name: "location",
    type: "select",
    label: "Standort",
    placeholder: "Standort auswählen...",
    colSpan: 1,
  },
  {
    name: "make",
    type: "select",
    label: "Marke",
    placeholder: "Um welche Marke geht es?",
    colSpan: 1,
  },
  {
    name: "company",
    type: "text",
    label: "Firma",
    placeholder: "Firma",
  },
  {
    name: "salutation",
    type: "select",
    label: "Anrede",
    placeholder: "Bitte auswählen...",
    options: [
      { value: "Herr", label: "Herr" },
      { value: "Frau", label: "Frau" },
      { value: "Divers", label: "Divers" },
    ],
  },
  {
    name: "firstname",
    type: "name",
    label: "Vorname",
    placeholder: "Vorname",
  },
  {
    name: "lastname",
    type: "name",
    label: "Name",
    placeholder: "Nachname",
  },
  {
    name: "email",
    type: "text",
    inputType: "email",
    label: "E-Mail",
    placeholder: "E-Mail",
  },
  {
    name: "phone",
    type: "phone",
    label: "Telefon",
    placeholder: "+49 (123) 45678902",
  },
];

const vehicleFields: ContactFormFieldConfigT<ServiceContactFormFieldNameT>[] = [
  {
    name: "vehicleLicensePlate",
    type: "text",
    label: "Kennzeichen",
    placeholder: "Kennzeichen",
  },
  {
    name: "vehicleVin",
    type: "text",
    label: "FIN",
    placeholder: "FIN",
  },
  {
    name: "vehicleMileage",
    type: "mileage",
    label: "Kilometerstand",
    placeholder: "Kilometerstand",
    unit: "km",
  },
  {
    name: "vehicleModel",
    type: "text",
    label: "Modell",
    placeholder: "Modellbeschreibung",
  },
];

const commentsField: ContactFormFieldConfigT<ServiceContactFormFieldNameT> = {
  name: "comments",
  type: "textarea",
  label: "Gibt es weitere Bemerkungen oder Hinweise?",
  placeholder: "Ihre Nachricht...",
  colSpan: 2,
};

const privacyField: ContactFormFieldConfigT<ServiceContactFormFieldNameT> = {
  name: "privacy",
  type: "checkbox",
  colSpan: 2,
  label: (
    <span className="text-sm leading-relaxed whitespace-normal sm:whitespace-nowrap">
      Mit absenden bestätigen Sie unsere{" "}
      <Link href="/datenschutz" className="underline">
        Datenschutzerklärung
      </Link>
      .
    </span>
  ),
};

const honeypotField: ContactFormFieldConfigT<ServiceContactFormFieldNameT> = {
  name: "mail",
  type: "honeypot",
};

const caseField: ContactFormFieldConfigT<ServiceContactFormFieldNameT> = {
  name: "case",
  type: "select",
  hidden: true,
  options: [
    { value: "CASE", label: "CASE" },
    { value: "OPPORTUNITY", label: "OPPORTUNITY" },
  ],
};

const urlField: ContactFormFieldConfigT<ServiceContactFormFieldNameT> = {
  name: "URL",
  type: "text",
  hidden: true,
};

export const serviceContactFormConfig: ContactFormConfigT<ServiceContactFormFieldNameT> =
  {
    sections: [
      {
        id: "primary",
        layout: "grid",
        columns: 2,
        fields: primaryFields,
      },
      {
        id: "vehicle",
        title: "Fahrzeugdaten (optional)",
        collapsible: true,
        defaultExpanded: false,
        layout: "grid",
        columns: 2,
        fields: vehicleFields,
      },
      {
        id: "additional",
        layout: "grid",
        columns: 2,
        fields: [
          commentsField,
          privacyField,
          honeypotField,
          caseField,
          urlField,
        ],
      },
    ],
    honeypotFieldName: "mail",
    submitButtonLabel: "Service-Anfrage senden",
    submittingLabel: "Wird gesendet...",
    successToast: {
      title: "Nachricht erfolgreich gesendet",
      description: "Wir melden uns zeitnah zu Ihrer Service-Anfrage.",
    },
    errorToast: {
      title: "Fehler beim Senden",
      description:
        "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
    },
  };
